import {
  Banknote,
  BriefcaseBusiness,
  Check,
  ChevronDown,
  ChevronUp,
  Landmark,
  Pencil,
  Send,
  ShieldCheck,
  UserRoundCheck,
} from "lucide-react";
import { useMemo, useState } from "react";

import { cn } from "../../../../lib/utils";
import type {
  ManualEntryKind,
  ManualEntryRequest,
  ManualFinancialEntry,
} from "../../../../lib/onboarding-api";
import {
  useProfileEdit,
  type ProfileEditPatch,
  type UseProfileEditOptions,
  type UseProfileEditResult,
} from "../../../../lib/profile/use-profile-edit";
import type { ProfileRowId } from "../../../../lib/readiness/factor-routing";
import { SharpenPill } from "../../../readiness/sharpen-pill";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Select } from "../../../ui/select";
import { TierBadge } from "../../../ui/tier-badge";
import { ManualEntryList } from "./manual-entry-list";
import type { ProfileRowTier } from "./tier-dot";
import { TierLadder } from "./tier-ladder";
import { ladderUnavailable } from "./aggregate-input-row.helpers";

export interface SharpenGoal {
  projected_delta: number;
  action_label: string;
  // Carries the cap/set-plan/normal discrimination from sharpenMetaForRow.
  // Default ("delta") keeps the legacy "+N pts" rendering for back-compat
  // with any caller that hasn't been updated yet.
  variant?: import("../../../readiness/sharpen-pill").SharpenPillVariant;
}

export interface AggregateInputRowEdit {
  endAdornment?: string;
  field: keyof ProfileEditPatch;
  formatValue?: (value: string) => string;
  inputType: "currency" | "select";
  label: string;
  max?: number;
  min?: number;
  options?: Array<{ label: string; value: string }>;
  parseValue?: (value: string) => number | string;
  placeholder?: string;
  value: number | string | null;
}

// Manual entries surface attached to a row. When set, the expanded view
// renders a list of additional sources (rental income, secondary debts,
// other bank accounts) beneath the scalar editor. Each row owns its own
// kind (income / asset / liability) so the form knows which fields apply.
export interface AggregateInputRowManualEntries {
  kind: ManualEntryKind;
  entries: ManualFinancialEntry[];
  isLoading: boolean;
  addLabel: string;
  emptyMessage: string;
  sectionLabel: string;
  onCreate: (req: ManualEntryRequest) => Promise<void>;
  onUpdate: (
    id: string,
    req: Omit<ManualEntryRequest, "entry_kind">,
  ) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  // 2026-05-19 finances handoff: inline verification CTAs next to the
  // "+ Add" button — same handlers as the section-header pair. Both
  // optional; ManualEntryList only renders the CTAs when both are
  // present so we don't ship a half-pair.
  //
  // bankComingSoon (added 2026-05-28) replaces the Connect bank affordance
  // with a non-clickable "Coming soon" pill. Also disables the bank-verified
  // rung on this row's TierLadder so both verification surfaces tell the
  // same story. Used for income while we migrate from legacy
  // income_verification → Plaid Check Bank Income (see backend
  // gateway_plaid.go CreateLinkToken).
  onVerifyConnectBank?: () => void;
  onVerifyUploadDocs?: () => void;
  bankComingSoon?: boolean;
}

export interface AggregateInputRowProps {
  edit?: AggregateInputRowEdit;
  rowId: ProfileRowId;
  label: string;
  summary: string;
  tier: ProfileRowTier;
  sharpenHint?: string;
  // sharpenGoal renders as a clickable SharpenPill inside the expanded
  // Trust region. Pulling the score nudge out of the collapsed header
  // keeps the row scannable and reserves the chevron-open surface as the
  // place where motivational, achievable next-steps live.
  sharpenGoal?: SharpenGoal;
  onAskSill?: () => void;
  opts?: UseProfileEditOptions;
  // SILL-304: when the user climbs to BANK on this row's tier ladder,
  // open Plaid Link scoped to this input's product. Personal & ID rows
  // auto-disable the BANK pill regardless of this callback (Plaid
  // doesn't cover identity).
  onConnectBank?: () => void;
  // SILL-304: when the user clicks the DOC rung, route them to the
  // documents view where they can upload evidence. Optional so test
  // renders and the ComponentsPreviewPage can no-op; missing handler
  // leaves the DOC pill clickable but inert (a follow-up may want to
  // disable it instead — for now keeping consistent ladder shape).
  onUploadDocs?: () => void;
  /**
   * Task 1678: whether this input can be verified by a bank connection /
   * document upload, as answered by the server
   * (GET /api/verification/field-capabilities), plus the server's reason
   * when it cannot. Replaces the local BANK_DISABLED_ROWS set, and gives the
   * DOC rung the gate it never had.
   *
   * Both booleans default to FALSE — fail closed. A caller that has not yet
   * resolved the capability table renders no climb rather than one it may
   * not be able to honour; over-offering is the defect this task fixes.
   *
   * The reasons are the server's words, passed through to the ladder tooltip
   * unedited. This component must not compose its own — see TierLadder's
   * `unavailable` doc for the contradiction that caused.
   */
  supportsBank?: boolean;
  supportsDocument?: boolean;
  bankReason?: string;
  documentReason?: string;
  // SILL-304 follow-up: true while the BANK connect is in flight (token
  // mint + Plaid Link handshake). Drives the BANK pill's loading state
  // so the user sees their click registered during the ~500ms-1s window
  // before Plaid Link opens. Without it, an MFA-stale token-mint that
  // pops Clerk's reverification modal can look like a dead button.
  bankConnectLoading?: boolean;
  // SILL-302: optional list of manual financial entries to render in
  // the expanded view. Only the three financial rows (income,
  // cash_assets, debts) opt into this — employment and personal & ID
  // rows skip it.
  manualEntries?: AggregateInputRowManualEntries;
  /**
   * When provided, clicking the row's edit affordance (pencil icon) calls
   * this instead of expanding inline. The EditAndVerifyDrawer is then
   * responsible for rendering the full edit + sources experience.
   *
   * When absent, the existing expand-in-place behavior is preserved
   * (backwards-compatible with tests that exercise the inline editor).
   */
  onEdit?: () => void;
}

export function AggregateInputRow({ opts, ...props }: AggregateInputRowProps) {
  if (opts) {
    return <EditableAggregateInputRow {...props} opts={opts} />;
  }
  return <AggregateInputRowContent {...props} editProfile={null} />;
}

function EditableAggregateInputRow({
  opts,
  ...props
}: Omit<AggregateInputRowProps, "opts"> & { opts: UseProfileEditOptions }) {
  const editProfile = useProfileEdit(opts);
  return <AggregateInputRowContent {...props} editProfile={editProfile} />;
}

function AggregateInputRowContent({
  edit,
  rowId,
  label,
  summary,
  tier,
  sharpenHint,
  sharpenGoal,
  onAskSill,
  onConnectBank,
  onUploadDocs,
  supportsBank = false,
  supportsDocument = false,
  bankReason,
  documentReason,
  bankConnectLoading,
  manualEntries,
  onEdit,
  editProfile,
}: Omit<AggregateInputRowProps, "opts"> & {
  editProfile: UseProfileEditResult | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const initialDraft = edit?.value == null ? "" : String(edit.value);
  const [draftState, setDraftState] = useState({
    base: initialDraft,
    value: initialDraft,
  });
  const draft =
    draftState.base === initialDraft ? draftState.value : initialDraft;
  const setDraft = (value: string) => {
    setDraftState({ base: initialDraft, value });
  };
  const canEdit = tier === "self-reported" && Boolean(edit);
  const displayValue = edit?.formatValue ? edit.formatValue(draft) : summary;
  const error = editProfile?.error
    ? "Couldn't save — please try again."
    : undefined;
  const tierComposition = useMemo(
    () =>
      manualEntries && manualEntries.entries.length > 0
        ? buildTierComposition(manualEntries.entries)
        : [],
    [manualEntries],
  );

  const commitDraft = (nextRaw: string) => {
    if (!edit) return;
    if (nextRaw === initialDraft) {
      setDraft(nextRaw);
      return;
    }
    if (nextRaw === "") {
      return;
    }
    const parsed = edit.parseValue ? edit.parseValue(nextRaw) : Number(nextRaw);
    if (typeof parsed === "number" && !Number.isFinite(parsed)) {
      return;
    }
    setDraft(nextRaw);
    if (!editProfile) {
      return;
    }
    editProfile
      .mutate({ [edit.field]: parsed } as ProfileEditPatch)
      .catch(() => {
        setDraft(initialDraft);
      });
  };

  return (
    <article
      // TODO(SILL-187 follow-up): accept idPrefix prop if ProfileView is ever embedded twice on a page.
      id={`profile-row-${rowId}`}
      data-state={expanded ? "open" : "closed"}
      className="overflow-hidden p-0 transition-colors hover:bg-accent/35"
    >
      <button
        type="button"
        onClick={onEdit ? onEdit : () => setExpanded((v) => !v)}
        className="flex min-h-[4.5rem] w-full items-center justify-between gap-3 px-4 py-3.5 text-left sm:px-5"
        aria-label={label}
        aria-expanded={onEdit ? undefined : expanded}
        data-slot="interactive-control"
      >
        {/* LEFT: icon tile + title + detail */}
        <span className="flex min-w-0 items-center gap-3">
          <span
            data-testid="aggregate-input-icon"
            data-tier={tier}
            className={cn(
              "grid size-9 shrink-0 place-items-center rounded-xl",
              tier === "self-reported"
                ? "bg-muted text-muted-foreground"
                : "bg-primary/10 text-primary",
            )}
          >
            <InputRowGlyph rowId={rowId} />
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-foreground">
              {label}
            </span>
            <span className="block truncate text-xs text-muted-foreground mt-0.5">
              {displayValue}
            </span>
          </span>
        </span>
        {/* RIGHT: verification badge + edit affordance */}
        <span className="flex min-w-0 shrink-0 items-center justify-end gap-2">
          {tierComposition.length > 0 ? (
            <TierCompositionChips composition={tierComposition} />
          ) : (
            <TierBadge size="sm" tier={tier} />
          )}
          {onEdit ? (
            /* Drawer mode: pencil icon opens the Edit & Verify drawer */
            <span
              role="button"
              tabIndex={0}
              aria-label={`Edit ${label}`}
              data-slot="row-edit-trigger"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  onEdit();
                }
              }}
              className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
            </span>
          ) : (
            /* Expand-in-place mode: chevron */
            expanded ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )
          )}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-border-soft bg-background/60 px-4 pb-4 pt-4 sm:px-5">
          {/* Value region — the editor and any value-relevant copy. */}
          {canEdit && edit ? (
            <div className="max-w-xl space-y-3" data-slot="value-region">
              {edit.inputType === "select" && edit.options ? (
                <Select
                  disabled={Boolean(editProfile?.isPending)}
                  error={error}
                  items={edit.options}
                  label={edit.label}
                  onValueChange={commitDraft}
                  placeholder={edit.placeholder}
                  value={draft}
                />
              ) : (
                <Input
                  aria-label={edit.label}
                  className="font-bold tabular-nums"
                  defaultValue={draft}
                  disabled={Boolean(editProfile?.isPending)}
                  endAdornment={edit.endAdornment}
                  error={error}
                  inputMode="numeric"
                  key={initialDraft}
                  label={edit.label}
                  max={edit.max}
                  min={edit.min}
                  onBlur={(event) => commitDraft(event.currentTarget.value)}
                  placeholder={edit.placeholder}
                  startAdornment="$"
                  type="number"
                />
              )}
            </div>
          ) : null}

          {/* Sources region — supplementary manual entries (rental income,
              secondary debts, other bank accounts) plus any doc-backed
              entries rendered read-only. Only rendered for the three
              financial rows that wire up manualEntries. */}
          {manualEntries ? (
            <div
              className={cn(
                "space-y-2",
                canEdit && edit
                  ? "mt-4 border-t border-border-soft pt-4"
                  : null,
              )}
              data-slot="sources-region"
            >
              <p className="eyebrow-label text-[10px] tracking-[0.18em]">
                {manualEntries.sectionLabel}
              </p>
              <ManualEntryList
                kind={manualEntries.kind}
                entries={manualEntries.entries}
                isLoading={manualEntries.isLoading}
                addLabel={manualEntries.addLabel}
                emptyMessage={manualEntries.emptyMessage}
                onVerifyConnectBank={manualEntries.onVerifyConnectBank}
                onVerifyUploadDocs={manualEntries.onVerifyUploadDocs}
                bankComingSoon={manualEntries.bankComingSoon}
                onCreate={manualEntries.onCreate}
                onUpdate={manualEntries.onUpdate}
                onDelete={manualEntries.onDelete}
              />
            </div>
          ) : null}

          {/* Trust region — verification ladder, next-step prompt, score nudge,
              Ask Sill. Bottom-stacked so the eye lands on the editor first. */}
          <div
            className={cn(
              "space-y-3",
              (canEdit && edit) || manualEntries
                ? "mt-4 border-t border-border-soft pt-4"
                : null,
            )}
            data-slot="trust-region"
          >
            <div className="space-y-2">
              <p className="eyebrow-label text-[10px] tracking-[0.18em]">
                Verification
              </p>
              <TierLadder
                unavailable={ladderUnavailable({
                  supportsBank,
                  supportsDocument,
                  bankReason,
                  documentReason,
                  bankComingSoon: manualEntries?.bankComingSoon ?? false,
                })}
                bankLoading={bankConnectLoading}
                current={tier}
                onClimb={(target) => {
                  // SILL-304 dead-link audit: route each climb target to
                  // the path that actually produces that verification
                  // evidence. Document-backed → documents view (where the
                  // user uploads); bank-verified → Plaid Link. Lender
                  // stays locked at the ladder level so no handler runs.
                  if (target === "document-backed") {
                    onUploadDocs?.();
                  } else if (target === "bank-verified") {
                    onConnectBank?.();
                  }
                }}
              />
              {sharpenHint && (
                <p className="text-sm text-muted-foreground">
                  Next: {sharpenHint}
                </p>
              )}
            </div>
            {sharpenGoal && (
              <SharpenPill
                delta={sharpenGoal.projected_delta}
                action={sharpenGoal.action_label}
                variant={sharpenGoal.variant}
              />
            )}
            {onAskSill && (
              <Button
                type="button"
                onClick={onAskSill}
                className="rounded-pill"
                size="sm"
                variant="ghost"
                aria-label="Ask Sill about this"
              >
                <Send className="h-3.5 w-3.5" /> Ask Sill
              </Button>
            )}
          </div>
        </div>
      )}
    </article>
  );
}

function InputRowGlyph({ rowId }: { rowId: ProfileRowId }) {
  const props = {
    "aria-hidden": true,
    className: "size-4",
    strokeWidth: 2,
  } as const;

  switch (rowId) {
    case "income":
      return <Banknote {...props} />;
    case "employment":
      return <BriefcaseBusiness {...props} />;
    case "cash_assets":
      return <Landmark {...props} />;
    case "debts":
      return <ShieldCheck {...props} />;
    case "personal_identity":
      return <UserRoundCheck {...props} />;
    default:
      return <ShieldCheck {...props} />;
  }
}

type TierComposition = Array<{ tier: ProfileRowTier; count: number }>;

const TIER_ORDER: ProfileRowTier[] = [
  "self-reported",
  "document-backed",
  "bank-verified",
  "lender-approved",
];

function buildTierComposition(
  entries: ManualFinancialEntry[],
): TierComposition {
  const counts = new Map<ProfileRowTier, number>();
  for (const entry of entries) {
    const tier = entryTier(entry);
    counts.set(tier, (counts.get(tier) ?? 0) + 1);
  }
  return TIER_ORDER.flatMap((tier) => {
    const count = counts.get(tier) ?? 0;
    return count > 0 ? [{ tier, count }] : [];
  });
}

function entryTier(entry: ManualFinancialEntry): ProfileRowTier {
  switch (entry.verification_tier) {
    case "document":
      return "document-backed";
    case "connected":
      return "bank-verified";
    case "pre_approval":
      return "lender-approved";
    case "self_reported":
      return "self-reported";
  }

  const sourceFamily = entry.source_family ?? entry.source;
  if (sourceFamily === "document") return "document-backed";
  if (sourceFamily === "plaid") return "bank-verified";
  return "self-reported";
}

// Tier label + tone for the composition pills. Uppercase + tracked-out
// to mirror `TierBadge` (the row-header badge when there's no composition)
// AND the verification ladder pills inside the expanded row — so the
// inputs section reads as one visual family of tier tags rather than
// two. The count joins via a normal-case `· N` suffix (see TierBadge's
// freshness suffix, line 165-172, for the same shape).
const TIER_COMPOSITION_LABEL: Record<ProfileRowTier, string> = {
  "self-reported": "Self-reported",
  "document-backed": "Document",
  "bank-verified": "Verified",
  "lender-approved": "Pre-approved",
};

// Tone classes parallel TierBadge's `tierTone` (on-light variant) so the
// hierarchy stays visually consistent across the row header and the
// in-content composition pills. We don't reuse TierBadge directly because
// TierBadge has no slot for a count suffix — its `verifiedAt`/freshness
// suffix is mutually exclusive with `· N`. Inlining the small markup
// keeps both pieces composable without growing TierBadge's API surface.
const TIER_COMPOSITION_TONE: Record<ProfileRowTier, string> = {
  "self-reported": "bg-muted text-muted-foreground",
  "document-backed": "bg-info/15 text-info",
  "bank-verified": "bg-primary/10 text-primary",
  "lender-approved": "bg-panel-ink text-panel-ink-foreground",
};

function TierCompositionChips({
  composition,
}: {
  composition: TierComposition;
}) {
  return (
    <span
      className="flex flex-wrap items-center gap-1.5"
      data-testid="tier-composition-chips"
    >
      {composition.map(({ tier, count }) => {
        const label = TIER_COMPOSITION_LABEL[tier];
        return (
          <span
            aria-label={`${label} · ${count} ${count === 1 ? "source" : "sources"}`}
            className={cn(
              "inline-flex min-h-6 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.1em]",
              TIER_COMPOSITION_TONE[tier],
            )}
            data-testid="tier-composition-item"
            data-tier={tier}
            key={tier}
          >
            {tier === "bank-verified" ? (
              <Check
                aria-hidden="true"
                className="size-3 shrink-0"
                strokeWidth={2.75}
              />
            ) : null}
            <span>{label}</span>
            <span
              aria-hidden="true"
              className="font-medium normal-case tracking-normal opacity-80"
              data-testid="tier-source-count"
            >
              · <span className="font-bold tabular-nums">{count}</span>
            </span>
          </span>
        );
      })}
    </span>
  );
}
